import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IAddContactNoteCallingActionPayload,
  type ICallingActionPayload,
  type IParamsCreateAICallingAgent,
  type IResponseFullAICallingAgent,
  type IResponseTemplate,
} from 'entities/AICalling/api/types'
import type { MergeField } from 'entities/MergeField/model/MergeField'
import { IntegrationKey } from 'entities/Integrations'
import { type IAgentHandoffNotifySettings } from 'entities/Chatbot'
import {
  addContactNoteTemplate,
  addConversationNoteTemplate,
  addTagTemplate,
  createActionChip,
  handoffToHumanTemplate,
  type IAgentAction,
  removeTagTemplate,
  sendSMSTemplate,
} from 'entities/TemplateActions/actions.tsx'
import {
  EnumVariantMessageField,
  mergeFieldReplacer,
  MessageFieldStore,
} from 'widgets/MessageField'
import { type IAddContactNoteActionProps } from 'widgets/TemplateActions/addContactNote/addContactNote.tsx'
import type { IAddTagContentData } from 'widgets/TemplateActions/ui/AddTagContent/AddTagContent.tsx'
import { createHandOfToHumanCallingAction } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/handofToHuman/createHandOfToHumanCallingAction.ts'
import { createAddConversationNoteCallingAction } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/addConversationNote/createAddConversationNoteCallingAction.ts'
import { createAddContactNoteCallingAction } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/addContactNote/createAddContactNoteCallingAction.ts'
import { createAddTagCallingAction } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/addTag/createAddTagCallingAction.ts'
import { createRemoveTagCallingAction } from 'pages/chatbot/pages/calling/ui/AICallingAgentManage/ui/AgentInstructions/actions/removeTag/createRemoveTagCallingAction.ts'

export class AgentInstructionsStore {
  private _defaultInstructions
  readonly messageFieldStore: MessageFieldStore
  readonly mergeFieldConfig = {
    isAllIntegration: true,
    includeIntegrations: [IntegrationKey.hubspot],
  }

  constructor(
    readonly entities: IEntitiesStore,
    readonly parent: {
      template?: IResponseTemplate
      response?: IResponseFullAICallingAgent | null
      params: IParamsCreateAICallingAgent | null
      actionsMap: Map<string, ICallingActionPayload>
    },
    readonly initialInstruction?: string
  ) {
    this._defaultInstructions = parent.template?.instructions || ''

    this.messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Set your agent’s role, response style, and add any specific instructions.',
        noCounter: true,
        styles: {
          minHeight: 200,
          maxHeight: 372,
        },
        variant: EnumVariantMessageField.Clean,
        disableMergeFieldsFallback: true,
        makeEvents: () => ({
          onFocus: this.removeError,
        }),
      },
      this.entities
    )
    const templateInstructions = this.initialInstruction
      ? this.replaceSectionContent(this._defaultInstructions, 'Role', this.initialInstruction)
      : this._defaultInstructions

    this.setInstruction(
      (parent.response
        ? parent.response?.instructions
        : templateInstructions || this.initialInstruction) || ''
    )

    makeAutoObservable(this)
  }

  init = () => {
    this.reactionTemplate()
    return () => {
      this._disposeReactionTemplate?.()
    }
  }

  private _disposeReactionTemplate: IReactionDisposer | null = null
  reactionTemplate = () => {
    this._disposeReactionTemplate?.()
    this._disposeReactionTemplate = reaction(
      () => this.parent.template,
      (template) => {
        if (!template) return
        this.setInstruction(template.instructions)
        this._defaultInstructions = template.instructions
      }
    )
  }

  filterActions = (action: ICallingActionPayload): boolean => {
    switch (action.action_type) {
      case 'add_tag':
      case 'remove_tag':
      case 'add_contact_note':
      case 'add_conversation_note':
      case 'send_message':
      case 'hand_of_to_human': {
        return this.instructions.includes(action.settings.token_id)
      }
    }
  }

  private _error = ''
  get error() {
    return this._error
  }
  setError = (value: string) => {
    this._error = value
  }
  removeError = () => {
    this.setError('')
  }

  get instructions() {
    return this.messageFieldStore.text
  }
  setInstruction = (instruction: string) => {
    this.messageFieldStore.setMessageData({
      message: instruction,
      attachments: [],
      isFocus: false,
      isReplace: true,
      isReset: false,
      replaceActionChips: true,
      isDisableReplaceMention: true,
    })
    this.removeError()
  }

  resetInstructions = () => {
    this.setInstruction(this._defaultInstructions)
  }

  checkValidate = () => {
    let validate = true
    if (!this.instructions.length) {
      validate = false
      this.setError('Add instructions for your agent')
    }
    return validate
  }
  onChooseMergeField = (field: MergeField, replaceContent?: string) => {
    if (!this.messageFieldStore.addContent) return

    const fieldContent = mergeFieldReplacer(this.entities, field.template) + '&nbsp;'

    this.messageFieldStore.addContent(fieldContent, replaceContent)
  }
  onAddAction = (action: IAgentAction) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.addContent(createActionChip(action) + '&nbsp;', '/')
  }
  private _deleteAction = (chip: HTMLSpanElement) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.removeActionChip(chip)
  }
  deleteChipAndAgentAction = (chip: HTMLSpanElement, action: ICallingActionPayload) => {
    this._deleteAction(chip)
    this._deleteAgentAction(action.settings.token_id)
  }

  onUpdateAgentAction = (action: ICallingActionPayload) => {
    this.parent.actionsMap.set(action.settings.token_id, action)
  }
  getAgentAction = (tokenId: string) => this.parent.actionsMap.get(tokenId)
  private _deleteAgentAction = (tokenId: string) => this.parent.actionsMap.delete(tokenId)

  onAddTagAction = (data: IAddTagContentData) => {
    const action = createAddTagCallingAction(data)
    this.parent.actionsMap.set(action.settings.token_id, action)
    this.onAddAction(addTagTemplate.createAction({ token_id: action.settings.token_id }))
  }

  onRemoveTagAction = (data: IAddTagContentData) => {
    const action = createRemoveTagCallingAction(data)
    this.parent.actionsMap.set(action.settings.token_id, action)
    this.onAddAction(removeTagTemplate.createAction({ token_id: action.settings.token_id }))
  }

  onAddContactNoteAction = (data: IAddContactNoteActionProps['data']) => {
    const action: IAddContactNoteCallingActionPayload = createAddContactNoteCallingAction(data)
    this.parent.actionsMap.set(action.settings.token_id, action)
    this.onAddAction(addContactNoteTemplate.createAction({ token_id: action.settings.token_id }))
  }

  onAddConversationNoteAction = (data: IAddContactNoteActionProps['data']) => {
    const action = createAddConversationNoteCallingAction(data)
    this.parent.actionsMap.set(action.settings.token_id, action)
    this.onAddAction(
      addConversationNoteTemplate.createAction({ token_id: action.settings.token_id })
    )
  }

  onAddSMSAction = (action: ICallingActionPayload) => {
    this.parent.actionsMap.set(action.settings.token_id, action)
    this.onAddAction(sendSMSTemplate.createAction({ token_id: action.settings.token_id }))
  }

  onAddHandoffToHumanAction = (data: IAgentHandoffNotifySettings) => {
    const action = createHandOfToHumanCallingAction(data)
    this.parent.actionsMap.set(action.settings.token_id, action)
    this.onAddAction(handoffToHumanTemplate.createAction({ token_id: action.settings.token_id }))
  }

  replaceSectionContent = (template: string, sectionTitle: string, content: string) => {
    const heading = `## ${sectionTitle}`
    const [first, ...rest] = template.split('\n## ')
    const sections = [first, ...rest.map((section) => `## ${section}`)]

    return sections
      .map((section) => (section.startsWith(heading) ? `${heading}\n\n${content}\n` : section))
      .join('\n')
  }
}
